import { Monitor, Moon, Sun } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useShellStore, type ShellThemeMode } from '@/store/shell'
import { cn } from '@/utils/cn'

const themeIcons: Record<ShellThemeMode, typeof Sun> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
}

const compactThemeLabels: Record<ShellThemeMode, string> = {
  system: 'Auto',
  light: 'Clair',
  dark: 'Nuit',
}

interface ThemeSwitcherProps {
  compact?: boolean
}

export default function ThemeSwitcher({ compact = false }: ThemeSwitcherProps) {
  const { t } = useTranslation()
  const themeMode = useShellStore((state) => state.themeMode)
  const setThemeMode = useShellStore((state) => state.setThemeMode)
  const CurrentIcon = themeIcons[themeMode]

  return (
    <label
      className={cn(
        'flex items-center gap-2 text-sm text-muted-foreground',
        compact &&
          'rounded-2xl border border-border/80 bg-card/88 px-3 py-2 shadow-sm backdrop-blur-sm',
      )}
    >
      <CurrentIcon className="h-4 w-4" />
      {!compact && <span>{t('theme.label')}</span>}
      <select
        value={themeMode}
        onChange={(event) => setThemeMode(event.target.value as ShellThemeMode)}
        className={cn(
          'rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground',
          compact &&
            'w-[4.5rem] border-0 bg-transparent px-0 py-0 font-medium shadow-none focus:outline-none focus:ring-0',
        )}
        aria-label={t('theme.label')}
      >
        <option value="system">{compact ? compactThemeLabels.system : t('theme.system')}</option>
        <option value="light">{compact ? compactThemeLabels.light : t('theme.light')}</option>
        <option value="dark">{compact ? compactThemeLabels.dark : t('theme.dark')}</option>
      </select>
    </label>
  )
}
